/**
 * Widget-specific analytics tracking.
 * - Sends events to GTM via trackEvent (production/staging only)
 * - Sends events to parent window via postMessage (always - for partner tracking)
 * - Logs events to console in development (for debugging)
 *
 * All widget events use the compressed naming convention: UiWgt{Page}{Element}{Action}
 */
import { AnalyticsController } from 'analytics/analytics-controller';
import { isClientSide } from 'utils/host-config';
import {
  GTM_WIDGET_SEARCH_VIEW,
  GTM_WIDGET_DETAILS_VIEW,
  GTM_WIDGET_RESULT_VIEW,
  GTM_WIDGET_NO_ESTIMATE_VIEW,
  GTM_WIDGET_SEARCH_CLICK,
  GTM_WIDGET_GET_ESTIMATE_CLICK,
  GTM_WIDGET_BACK_CLICK,
  GTM_WIDGET_AGENT_CLICK,
  GTM_WIDGET_ERROR,
} from 'constants/events';

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Sends a widget event to the parent window via postMessage.
 * This allows partners to listen for and react to widget events.
 */
function sendEventToParent(event: string, clientId: string, additionalLabel?: string) {
  if (!isClientSide() || window === window.parent) return;

  const data: Record<string, string> = {
    event,
    clientId,
    timestamp: new Date().toISOString(),
  };
  if (additionalLabel) data.detail = additionalLabel;

  window.parent.postMessage({
    type: 'zoocasa:widget:event',
    data,
  }, '*');
}

/**
 * Tracks a widget-specific event:
 * 1. GTM trackEvent (production/staging)
 * 2. postMessage to parent window (always)
 * 3. console.log (development only)
 */
function trackWidgetEvent(event: string, clientId: string, additionalLabel?: string) {
  const label = additionalLabel ? `${clientId}:${additionalLabel}` : clientId;

  // GTM (only fires in production/staging)
  AnalyticsController.trackEvent(event, 'Widget', label);

  // postMessage to parent (always - for partner tracking and test page)
  sendEventToParent(event, clientId, additionalLabel);

  // Console log in development
  if (isDevelopment && isClientSide()) {
    console.log(`[Widget Event] ${event}`, { clientId, detail: additionalLabel || undefined });
  }
}

/**
 * Pre-built widget event trackers.
 */
export const widgetEvents = {
  // --- View events (funnel) ---
  /** Search page loaded (step 1) */
  trackSearchView: (clientId: string) => trackWidgetEvent(GTM_WIDGET_SEARCH_VIEW, clientId),

  /** Property details page loaded (step 2) */
  trackDetailsView: (clientId: string) => trackWidgetEvent(GTM_WIDGET_DETAILS_VIEW, clientId),

  /** Results page loaded with estimate (step 3) */
  trackResultView: (clientId: string) => trackWidgetEvent(GTM_WIDGET_RESULT_VIEW, clientId),

  /** Results page loaded with no estimate */
  trackNoEstimateView: (clientId: string) => trackWidgetEvent(GTM_WIDGET_NO_ESTIMATE_VIEW, clientId),

  // --- Action events ---
  /** User selected an address in search */
  trackSearchClick: (clientId: string) => trackWidgetEvent(GTM_WIDGET_SEARCH_CLICK, clientId),

  /** User clicked "Get Estimate" on details form */
  trackGetEstimateClick: (clientId: string) => trackWidgetEvent(GTM_WIDGET_GET_ESTIMATE_CLICK, clientId),

  /** User clicked "Back to Search" */
  trackBackClick: (clientId: string) => trackWidgetEvent(GTM_WIDGET_BACK_CLICK, clientId),

  /** User clicked any agent contact / estimate CTA */
  trackAgentClick: (clientId: string) => trackWidgetEvent(GTM_WIDGET_AGENT_CLICK, clientId),

  // --- Error events ---
  /** Widget error (validation failure, load error, etc.) */
  trackError: (clientId: string, errorType: string) => trackWidgetEvent(GTM_WIDGET_ERROR, clientId, errorType),
};
